import { useContext, useMemo, useReducer, useState } from 'react'
import clsx from 'clsx'
import { compose, setDisplayName } from 'react-recompose'
import { withRouter } from 'react-router'
import { FavoriteIcon } from '@anewgo/icons'
import { getPath } from '@anewgo/functions'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ButtonBase from '@mui/material/ButtonBase'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Link from '@mui/material/Link'
import DialogActions from '@mui/material/DialogActions'
import { ADD_PROSPECT_FAVORITES } from '../../graphql'
import { apolloClient } from '../../graphql/enhancers'
import { getPlanName } from '@anewgo/functions'
import {
  EVT_CLICKED_SIGN_IN,
  EVT_LINKED_TO_FAVORITE,
  EVT_TOGGLED_COMPARE_FAVORITES,
} from '../../constants/eventTracking'

import { StoreContext } from '../../store'
import * as reducers from '../../store/reducers'
import {
  selectionLinkUrl,
  getReservationStatus,
  hasAnyProspectSignedFavoritesReservation,
} from '../../utils'
import { useDeleteFavorite, useEventTracker } from '../../utils/hooks'
import { requireProps } from '../helper'
import { withClient } from '../../hocs'
import ModalDrawer from '../ModalDrawer/ModalDrawer'
import ProductListItemWrapper from '../ProductListItemWrapper/ProductListItemWrapper'
import { BuilderStatus } from '../../constants/BuilderStatus'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import red from '@mui/material/colors/red'
import Alert from '@mui/material/Alert'
import {
  badgeClasses,
  StyledBadge,
  modalClasses,
  ModalStyles,
} from './FavoriteMenuStyles'
import FriendsFavoriteList from './FriendsFavoriteList/FriendsFavoriteList'
import LotStatus from '../../constants/lotStatus'

const FavoriteMenu = compose(
  withRouter,
  withClient,
  requireProps({ keys: ['client'] }),
  setDisplayName('FavoriteMenu')
)(
  ({
    client,
    history,
    openDrawer,
    setOpenDrawer,
    shareDialogHandler,
    isMobile,
    isShareDialogOpen,
  }) => {
    const track = useEventTracker()
    const { dispatch, favorites, isSignedIn, uiConfig, prospect, selection } =
      useContext(StoreContext)
    const favoritesReceivedByProspect = prospect
      ? prospect.receivedFavorites
      : null
    const { iconSize } = uiConfig
    const [isInEditingMode, toggleEditingMode] = useReducer(
      (state) => !state,
      false
    )
    const [favoriteToDelete, setFavoriteToDelete] = useState(null)
    const [favoriteToDuplicate, setFavoriteToDuplicate] = useState(null)

    const [favoriteToEdit, setFavoriteToEdit] = useState(null)
    const [anchorEl, setAnchorEl] = useState(null)
    //const [promptSignIn, setPromptSignIn] = useState(false)

    const handleFavoriteDelete = useDeleteFavorite(
      favoriteToDelete,
      setFavoriteToDelete
    )

    const favoritesWithLinkUrls = useMemo(() => {
      return favorites
        .slice()
        .sort((a, b) => (a.id > b.id ? -1 : 1))
        .map((favorite) => ({
          ...favorite,
          linkUrl: selectionLinkUrl({
            ...favorite,
            client,
          }),
          reservationStatus: BuilderStatus.getBuilderStatusName(
            getReservationStatus(favorite, prospect)
          ),
          hasAnybodySigned: hasAnyProspectSignedFavoritesReservation(
            prospect?.onlineReservations,
            favorite.id
          ),
        }))
    }, [client, favorites, prospect])

    if (!client) return null

    const title =
      favoritesWithLinkUrls.length > 0
        ? 'Select a favorite to design'
        : 'You have no favorite plans. Select one to design or "like" a few for comparative shopping'

    const toggleDrawer = (open) => {
      if (!open && isInEditingMode) {
        // Always revert to non-editing mode when closing the drawer
        toggleEditingMode()
      }
      setOpenDrawer(open)
    }

    const handleFavoriteSelect =
      ({ linkUrl, ...favorite }) =>
      () => {
        if (!linkUrl) return
        const trackingProps = {
          communityName: favorite.community
            ? favorite.community.name
            : undefined,
          communityId: favorite.community ? favorite.community.id : undefined,
          planName: favorite.plan ? favorite.plan.name : undefined,
          planId: favorite.plan ? favorite.plan.id : undefined,
          elevationId: favorite.elevation ? favorite.elevation.id : undefined,
          elevationCaption: favorite.elevation
            ? favorite.elevation.caption
            : undefined,
          elevationActive: favorite.elevation
            ? favorite.elevation.active
            : undefined,
          lotId: favorite.lot ? favorite.lot.id : undefined,
          lotName: favorite.lot ? favorite.lot.name : undefined,
          lotAddress: favorite.lot ? favorite.lot.address : undefined,
          inventoryId: favorite?.lot?.inventory
            ? favorite.lot.inventory.id
            : undefined,
        }
        track(EVT_LINKED_TO_FAVORITE, {
          ...trackingProps,
          referrer: 'favorites_menu',
        })

        let mirror = uiConfig.mirror
        if (
          favorite.elevation?.svgMirroring &&
          selection.garagePosition &&
          favorite.garagePosition !== getPath('garagePosition')(selection)
        ) {
          mirror = !uiConfig.mirror
        }
        dispatch(reducers.restoreSelectionFromFavorite(favorite, mirror))
        history.push(linkUrl)

        toggleDrawer(false)
      }

    const handleOpenEditFavoriteDialog = (event, favorite) => {
      setFavoriteToEdit(favorite)
      setAnchorEl(event.currentTarget)
    }

    const handleCancelDeleteFavoriteDialog = () => {
      setFavoriteToDelete(null)
    }

    const handleCancelDuplicateFavoriteDialog = () => {
      setFavoriteToDuplicate(null)
    }

    const handleCancelEditFavoriteDialog = () => {
      setAnchorEl(null)
      setFavoriteToEdit(null)
    }

    const handleFavoriteDuplicate = async () => {
      const favorites = [favoriteToDuplicate]
      if (prospect) {
        const id = await apolloClient
          .mutate({
            mutation: ADD_PROSPECT_FAVORITES,
            variables: {
              clientName: client.altName,
              email: prospect.email,
              favorites: favorites.map((fav) => ({
                communityId: getPath('community', 'id')(fav),
                planId: getPath('plan', 'id')(fav),
                elevationId: getPath('elevation', 'id')(fav),
                lotId: getPath('lot', 'id')(fav),
                schemeId: getPath('scheme', 'id')(fav),
                colorSelections: getPath('colorSelections')(fav),
                fpOptSelections: getPath('fpOptSelections')(fav),
                interiorDesignSelections: JSON.stringify(
                  getPath('interiorDesignSelections')(fav)
                ),
                source: 'truss',
                garagePosition: getPath('garagePosition')(fav),
                isMirrored: getPath('isMirrored')(fav),
              })),
            },
          })
          .then((data) => {
            return getPath('data', 'addFavoritesForProspect')(data)
          })
        favoriteToDuplicate.id = getPath('length')(id) && id[0]
        let favoritesArray = []
        favoritesArray.push(favoriteToDuplicate)
        dispatch(reducers.duplicateFavorites(favoritesArray))
        dispatch(reducers.restoreSelectionFromFavorite(favoriteToDuplicate))
      } else {
        dispatch(reducers.duplicateFavorites(favorites))
      }
      setFavoriteToDuplicate(null)
    }

    const handleShowCompareFavorites = () => {
      track(EVT_TOGGLED_COMPARE_FAVORITES, {
        active: true,
      })
      if (!isSignedIn) {
        dispatch(
          reducers.setBuyerLoginDialog({
            open: true,
            message: 'Please sign-in to use this feature',
          })
        )
        // dispatch(reducers.setLoginNotificationDialogOpen(true))
        return
      }
      setOpenDrawer(false)
      history.push(
        `/client/${client.altName}/compareFavorites?email=${prospect.email}&name=${prospect.name}`
      )
    }

    const handleSignIn = () => {
      track(EVT_CLICKED_SIGN_IN, {
        referrer: 'favorites_menu',
      })
      dispatch(
        reducers.setBuyerLoginDialog({
          open: true,
          message: 'Please sign-in to use this feature',
        })
      )
      // dispatch(reducers.setLoginNotificationDialogOpen(true))
    }

    const openFavoritesMenu = () => {
      /* not checking if signed in
    if (!isSignedIn) {
      setPromptSignIn(true)
      return
    }
    */
      toggleDrawer(!openDrawer)
    }

    const isFavDisabled = (favorite) => {
      const lot = favorite?.lot
      const elevation = favorite?.elevation
      const community = favorite?.community
      // If favorite is an inventory, it can use a retired elevation, thus we only check if the community is active
      if (lot && LotStatus.getInventoryStatuses().includes(lot.salesStatus)) {
        return !community?.active
      }
      // For the rest of the favorites, we check if the elevation and community are active
      return !(elevation?.active && community?.active && elevation)
    }

    const planToDelete = `${getPlanName(favoriteToDelete?.plan)} ${
      favoriteToDelete?.elevation.caption || ''
    }`.trim()

    // r e n d e r
    return (
      <>
        <StyledBadge
          color="primary"
          badgeContent={favoritesWithLinkUrls.length}
          classes={{ badge: badgeClasses.badge }}
          data-testid="favorite_menu_favorites_button"
        >
          <ClickAwayListener
            onClickAway={() => {
              !isShareDialogOpen && openDrawer && toggleDrawer(false)
            }}
          >
            <div className={badgeClasses.root} id="wrapper">
              <Dialog
                open={Boolean(favoriteToDelete)}
                onClose={handleCancelDeleteFavoriteDialog}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle>
                  Are you sure you want to delete <b>{planToDelete}</b> from
                  your favorites?
                </DialogTitle>
                <DialogContent>
                  {favoriteToDelete?.reservationStatus === 'In Progress' &&
                    !favoriteToDelete.hasAnybodySigned && (
                      <Alert severity="warning">
                        This favorite has a reservation in progress. By choosing
                        to delete this favorite you will also delete the active
                        reservation. Are you sure you want to continue?
                      </Alert>
                    )}
                  <List>
                    {favoriteToDelete && (
                      <ProductListItemWrapper selection={favoriteToDelete} />
                    )}
                  </List>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleCancelDeleteFavoriteDialog}
                    color="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFavoriteDelete}
                    color="primary"
                    autoFocus
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={Boolean(favoriteToDuplicate)}
                onClose={handleCancelDuplicateFavoriteDialog}
              >
                <DialogTitle>Duplicate this favorite?</DialogTitle>
                <DialogContent>
                  <List>
                    {favoriteToDuplicate && (
                      <ProductListItemWrapper selection={favoriteToDuplicate} />
                    )}
                  </List>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleCancelDuplicateFavoriteDialog}
                    color="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFavoriteDuplicate}
                    color="primary"
                    autoFocus
                  >
                    Duplicate
                  </Button>
                </DialogActions>
              </Dialog>
              <Menu
                open={Boolean(favoriteToEdit)}
                anchorEl={anchorEl}
                onClose={handleCancelEditFavoriteDialog}
              >
                <MenuItem
                  onClick={() => {
                    setFavoriteToDuplicate(favoriteToEdit)
                    handleCancelEditFavoriteDialog()
                  }}
                >
                  Duplicate
                </MenuItem>
                <MenuItem
                  // We do NOT want to allow deletion of favorite for these reservation statuses.
                  disabled={
                    (favoriteToEdit?.reservationStatus === 'In Progress' &&
                      favoriteToEdit.hasAnybodySigned) ||
                    favoriteToEdit?.reservationStatus === 'Approved'
                  }
                  onClick={() => {
                    setFavoriteToDelete(favoriteToEdit)
                    handleCancelEditFavoriteDialog()
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
              <ButtonBase
                className={badgeClasses.iconButton}
                onClick={() => openFavoritesMenu()}
              >
                <div className={badgeClasses.iconContainer}>
                  <FavoriteIcon
                    width={iconSize * 0.8}
                    height={iconSize * 0.8}
                    stopColor1={red['A700']}
                    stopColor2={red[100]}
                  />
                </div>
                <Typography
                  classes={{ button: badgeClasses.iconButtonText }}
                  variant={'button'}
                >
                  Favorites
                </Typography>
              </ButtonBase>
              <ModalDrawer
                sideDrawer={true}
                style={{ backgroundColor: '#F3F3F3' }}
                openDrawer={openDrawer}
                setOpenDrawer={toggleDrawer}
              >
                <ModalStyles>
                  <Typography className={modalClasses.title} variant={'body1'}>
                    {title}
                  </Typography>
                  {favoritesWithLinkUrls.length > 0 && (
                    <List className={modalClasses.favoritesList} dense>
                      {favoritesWithLinkUrls.map((favorite) => (
                        <ProductListItemWrapper
                          key={favorite.id || `${favorite.index}`}
                          button={!isInEditingMode && Boolean(favorite.linkUrl)}
                          onClick={
                            isInEditingMode
                              ? undefined
                              : handleFavoriteSelect(favorite)
                          }
                          selection={favorite}
                        >
                          <ListItemSecondaryAction>
                            <IconButton
                              aria-label="Options"
                              onClick={(event) => {
                                event.stopPropagation()
                                handleOpenEditFavoriteDialog(event, favorite)
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ProductListItemWrapper>
                      ))}
                    </List>
                  )}
                  <div className={modalClasses.actionItemsContainer}>
                    {favoritesWithLinkUrls.length > 1 && (
                      <div className={modalClasses.actionButtonRow}>
                        <Button
                          classes={{ root: modalClasses.actionButton }}
                          disabled={
                            favorites.length !== favoritesWithLinkUrls.length
                          }
                          variant="outlined"
                          color="primary"
                          onClick={handleShowCompareFavorites}
                        >
                          Compare
                        </Button>
                      </div>
                    )}
                    {favoritesWithLinkUrls.length > 0 && (
                      <div className={modalClasses.actionButtonRow}>
                        <Button
                          classes={{ root: modalClasses.actionButton }}
                          variant="outlined"
                          color="primary"
                          onClick={shareDialogHandler}
                        >
                          Share Favorites
                        </Button>
                      </div>
                    )}
                    {!isSignedIn && favoritesWithLinkUrls.length > 0 && (
                      <div className={modalClasses.actionButtonRow}>
                        <Button
                          variant="contained"
                          color="primary"
                          classes={{
                            root: clsx(
                              modalClasses.actionButton,
                              modalClasses.actionButtonLeft
                            ),
                          }}
                          onClick={handleSignIn}
                        >
                          Save for later
                        </Button>
                      </div>
                    )}
                  </div>
                  <FriendsFavoriteList
                    receivedFavorites={favoritesReceivedByProspect}
                    client={client}
                    prospectId={prospect?.id}
                  />
                </ModalStyles>
              </ModalDrawer>
            </div>
          </ClickAwayListener>
        </StyledBadge>
      </>
    )
  }
)

export default FavoriteMenu
